<template>
    <div class="ls-customer-table--container">
        <div class="ls-customer-table--row header">
            <div
                v-for="headerName in ['ID', 'Customer Name', 'Address', 'Email', 'Phone']"
                :key="headerName"
                class="ls-customer-table--cell"
                test-id="header-name"
            >
                {{ headerName }}
            </div>
        </div>
        <div v-for="(customer, customerIndex) in customers" :key="customer.id">
            <div
                v-for="(property, propertyIndex) in customer.properties"
                :key="property.id"
                :class="[
                    'ls-customer-table--row',
                    { odd: customerIndex % 2 !== 0, 'extra-property': propertyIndex },
                ]"
                test-id="row"
                @mouseover="setHoveredId({ id: property.id })"
                @mouseleave="setHoveredId({ id: null })"
                @click="onClick({ customer_id: customer.id, property_id: property.id })"
            >
                <div class="ls-customer-table--cell">
                    <span class="ls-customer-table--user-info">{{ customer.id }}</span>
                </div>
                <div class="ls-customer-table--cell">
                    <span class="ls-customer-table--user-info">
                        {{ customer.user.first_name }} {{ customer.user.last_name }}
                    </span>
                </div>
                <div v-if="!property.id" class="ls-customer-table--cell">
                    No address on file
                </div>
                <div v-else class="ls-customer-table--cell">
                    {{ property.street }}
                    <br v-if="screenSize.tablet" />
                    {{ property.city }}<span v-if="property.city">,</span>
                    {{ property.state }}
                    {{ property.zip }}
                </div>

                <div class="ls-customer-table--cell">
                    <span class="ls-customer-table--user-info">{{ customer.user.email }}</span>
                </div>
                <div class="ls-customer-table--cell" test-id="phone">
                    <span class="ls-customer-table--user-info">
                        {{ customer.user.phone | formattedPhone }}
                    </span>
                </div>
                <div class="ls-customer-table--cell">
                    <LsIcon
                        type="chevron"
                        size="md"
                        :color="hoveredId === property.id ? 'primary' : 'gray-primary'"
                    />
                </div>
            </div>
        </div>
        <LsPagination :pagination="pagination" :change-page="changePage" />
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import LsPagination from '../LsPagination/LsPagination.vue';
import ScreenSize from '@/graphql/queries/ScreenSize.gql';
import { LsIcon } from '@/components/common';

export default {
    name: 'LsCustomerTable',
    components: { LsIcon, LsPagination },
    props: {
        customers: VueTypes.arrayOf(
            VueTypes.shape({
                id: VueTypes.string.isRequired,
                properties: VueTypes.oneOfType([
                    VueTypes.arrayOf(
                        VueTypes.shape({
                            id: VueTypes.string.isRequired,
                            street: VueTypes.string.isRequired,
                            city: VueTypes.string.isRequired,
                            state: VueTypes.string.isRequired,
                            zip: VueTypes.string.isRequired,
                        }).loose
                    ),
                    VueTypes.arrayOf(VueTypes.shape({}).loose), // handle null id (no property on file)
                ]),
                user: VueTypes.shape({
                    email: VueTypes.string.isRequired,
                    first_name: VueTypes.string.isRequired,
                    last_name: VueTypes.string.isRequired,
                    phone: VueTypes.string.isRequired,
                }).isRequired.loose,
            }).loose
        ),
        pagination: VueTypes.shape({
            totalItems: VueTypes.number.isRequired,
            currentPage: VueTypes.number.isRequired,
            itemsPerPage: VueTypes.number.isRequired,
        }).isRequired.loose,
        changePage: VueTypes.func.isRequired,
    },
    data() {
        return {
            hoveredId: null,
            screenSize: {},
        };
    },
    apollo: {
        screenSize: ScreenSize,
    },
    methods: {
        onClick({ customer_id, property_id }) {
            if (!property_id) {
                return this.$router.push({
                    name: 'addProperty',
                    params: {
                        customer_id,
                    },
                });
            }
            this.$router.push({
                name: 'property',
                params: {
                    customer_id,
                    property_id,
                },
            });
        },
        setHoveredId({ id }) {
            this.hoveredId = id;
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-customer-table--container {
    font-size: $size-5;
    padding: 10px 10px 20px 10px;
    margin: 20px 0;
    color: $secondary;
    border-radius: $border-radius;
    box-shadow: $box-shadow;
}

.ls-customer-table--row {
    display: flex;
    justify-content: flex-start;
    border-top: 1px solid $table-border;
    font-weight: $light;
    &:hover {
        cursor: pointer;
    }
    &.odd {
        background-color: $secondary-lighter;
    }
    &.header {
        font-weight: $medium;
        border-top: none;
        cursor: default;
    }

    &.extra-property {
        .ls-customer-table--user-info {
            display: none;
        }
    }

    .ls-customer-table--cell {
        padding: 20px 15px 15px 15px;

        &:nth-child(1) {
            width: 7%;
        }
        &:nth-child(2) {
            width: 17%;
        }
        &:nth-child(3) {
            width: 34%;
        }
        &:nth-child(4) {
            width: 21%;
        }
        &:nth-child(5) {
            width: 16%;
        }
        &:nth-child(6) {
            width: 5%;
        }

        @media (max-width: $tablet-max-width) {
            &:nth-child(1) {
                width: 8%;
            }
            &:nth-child(2) {
                width: 20%;
            }
            &:nth-child(3) {
                width: 27%;
            }
            &:nth-child(4) {
                width: 20%;
            }
            &:nth-child(5) {
                width: 18%;
            }
            &:nth-child(6) {
                width: 7%;
            }
        }
    }
}
</style>
